import { RentalContract, MilestoneDateType, ContactType, ContractContact, MilestoneOrigin, ShowMilestoneInOverview } from "shared/types/rental-contracts";
import type { CellConfig } from "shared/components/UniversalTable/components/UniversalTableRow";
import { formatDate } from "shared/utils/get-dates";
import { applyMask } from "shared/utils/masks";
import { Chip, Button, Input, Tooltip } from "@heroui/react";
import { UserCircle, Calculator, FileText, Plus, Warning } from "@phosphor-icons/react";
import { useState } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { isIndexationUrgent, isIndexationRecent } from "../utils/indexationUtils";
import { calculateAnniversaryInfo } from "../utils/anniversaryUtils";
import { PriceEditButton } from "../components/PriceEditButton";
import { UnitType } from "shared/types/unit";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip as ShadcnTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Date picker component for adding/editing sign date to contracts
 */
const SignDatePicker = ({
  contractId,
  currentDate,
  milestoneId,
  onDateAdded
}: {
  contractId?: number;
  currentDate?: Date | string | null;
  milestoneId?: number;
  onDateAdded?: (contractId: number, date: string, newMilestoneId?: number) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [dateValue, setDateValue] = useState(currentDate ? new Date(currentDate).toISOString().split('T')[0] : '');
  const { session } = useSession();

  const handleSave = async () => {
    if (!dateValue || !contractId) return;

    setIsSaving(true);
    try {
      let newMilestoneId: number | undefined;

      if (milestoneId) {
        // Update existing milestone by deleting and creating new one
        // (since there's no update endpoint, we use the delete + create pattern)
        await fetchWithAuth(
          `${API_URL}/rental/milestone-dates/${milestoneId}`,
          { method: 'DELETE' },
          session
        );
      }

      // Create new milestone with the date
      const milestoneData = {
        milestoneDate: {
          rentalContractId: contractId,
          milestoneDateType: MilestoneDateType.SIGN_DATE,
          date: dateValue,
          origin: MilestoneOrigin.USER,
          showMilestoneInOverview: ShowMilestoneInOverview.NO as any
        }
      };

      const response = await fetchWithAuth(
        `${API_URL}/rental/add-contract-milestone-date`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(milestoneData)
        },
        session
      );

      if (response.ok) {
        const newMilestone = await response.json();
        newMilestoneId = newMilestone.id;

        toast.success(milestoneId ? 'Ondertekeningsdatum aangepast' : 'Ondertekeningsdatum toegevoegd');

        // Optimistic update - pass the new milestone ID back to parent
        onDateAdded?.(contractId, dateValue, newMilestoneId);
        setIsEditing(false);
      } else {
        throw new Error('Failed to save sign date');
      }
    } catch (error) {
      console.error('Error saving sign date:', error);
      toast.error('Fout bij opslaan van ondertekeningsdatum');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDateValue(currentDate ? new Date(currentDate).toISOString().split('T')[0] : '');
      setIsEditing(false);
    }
  };

  const handleBlur = () => {
    if (!isSaving && dateValue) {
      // Auto-save when user clicks away
      handleSave();
    } else if (!isSaving) {
      // Cancel if no value selected
      setIsEditing(false);
    }
  };

  if (!isEditing && currentDate) {
    return (
      <button
        onClick={() => setIsEditing(true)}
        className="text-sm hover:text-moss-primary transition-colors underline decoration-dotted cursor-pointer"
      >
        {formatDate(currentDate)}
      </button>
    );
  }

  if (!isEditing) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm whitespace-nowrap">-</span>
        <Button
          size="sm"
          isIconOnly
          variant="light"
          className="min-w-6 h-6"
          onPress={() => setIsEditing(true)}
        >
          <Plus weight="bold" size={16} className="text-neutral-secondary-mid" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <Input
        type="date"
        size="sm"
        className="max-w-[150px]"
        autoFocus
        value={dateValue}
        isDisabled={isSaving}
        onChange={(e) => setDateValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    </div>
  );
};

/**
 * Date picker component for adding/editing start date to contracts
 */
const StartDatePicker = ({
  contractId,
  currentDate,
  milestoneId,
  onDateAdded
}: {
  contractId?: number;
  currentDate?: Date | string | null;
  milestoneId?: number;
  onDateAdded?: (contractId: number, date: string, newMilestoneId?: number) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [dateValue, setDateValue] = useState(currentDate ? new Date(currentDate).toISOString().split('T')[0] : '');
  const { session } = useSession();

  const handleSave = async () => {
    if (!dateValue || !contractId) return;

    setIsSaving(true);
    try {
      let newMilestoneId: number | undefined;

      if (milestoneId) {
        // Update existing milestone by deleting and creating new one
        // (since there's no update endpoint, we use the delete + create pattern)
        await fetchWithAuth(
          `${API_URL}/rental/milestone-dates/${milestoneId}`,
          { method: 'DELETE' },
          session
        );
      }

      // Create new milestone with the date
      const milestoneData = {
        milestoneDate: {
          rentalContractId: contractId,
          milestoneDateType: MilestoneDateType.START_DATE,
          date: dateValue,
          origin: MilestoneOrigin.SYSTEM,
          showMilestoneInOverview: ShowMilestoneInOverview.YES as any
        }
      };

      const response = await fetchWithAuth(
        `${API_URL}/rental/add-contract-milestone-date`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(milestoneData)
        },
        session
      );

      if (response.ok) {
        const newMilestone = await response.json();
        newMilestoneId = newMilestone.id;

        toast.success(milestoneId ? 'Startdatum aangepast' : 'Startdatum toegevoegd');

        // Optimistic update - pass the new milestone ID back to parent
        onDateAdded?.(contractId, dateValue, newMilestoneId);
        setIsEditing(false);
      } else {
        throw new Error('Failed to save start date');
      }
    } catch (error) {
      console.error('Error saving start date:', error);
      toast.error('Fout bij opslaan van startdatum');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDateValue(currentDate ? new Date(currentDate).toISOString().split('T')[0] : '');
      setIsEditing(false);
    }
  };

  const handleBlur = () => {
    if (!isSaving && dateValue) {
      // Auto-save when user clicks away
      handleSave();
    } else if (!isSaving) {
      // Cancel if no value selected
      setIsEditing(false);
    }
  };

  if (!isEditing && currentDate) {
    return (
      <button
        onClick={() => setIsEditing(true)}
        className="text-sm hover:text-moss-primary transition-colors underline decoration-dotted cursor-pointer"
      >
        {formatDate(currentDate)}
      </button>
    );
  }

  if (!isEditing) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm whitespace-nowrap">-</span>
        <Button
          size="sm"
          isIconOnly
          variant="light"
          className="min-w-6 h-6"
          onPress={() => setIsEditing(true)}
        >
          <Plus weight="bold" size={16} className="text-neutral-secondary-mid" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <Input
        type="date"
        size="sm"
        className="max-w-[150px]"
        autoFocus
        value={dateValue}
        isDisabled={isSaving}
        onChange={(e) => setDateValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    </div>
  );
};

/**
 * Date picker component for adding indexation date (read-only, add-only)
 */
const IndexationDatePicker = ({
  contractId,
  currentDate,
  onDateAdded
}: {
  contractId?: number;
  currentDate?: Date | string | null;
  onDateAdded?: (contractId: number, date: string, newMilestoneId?: number) => void;
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [dateValue, setDateValue] = useState('');
  const { session } = useSession();

  const handleSave = async () => {
    if (!dateValue || !contractId) return;

    setIsSaving(true);
    try {
      // Create new indexation date milestone
      const milestoneData = {
        milestoneDate: {
          rentalContractId: contractId,
          milestoneDateType: MilestoneDateType.INDEXATION_DATE,
          date: dateValue,
          origin: MilestoneOrigin.SYSTEM,
          showMilestoneInOverview: ShowMilestoneInOverview.YES as any
        }
      };

      const response = await fetchWithAuth(
        `${API_URL}/rental/add-contract-milestone-date`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(milestoneData)
        },
        session
      );

      if (response.ok) {
        const newMilestone = await response.json();
        toast.success('Indexatiedatum toegevoegd');
        onDateAdded?.(contractId, dateValue, newMilestone.id);
        setIsAdding(false);
        setDateValue('');
      } else {
        throw new Error('Failed to save indexation date');
      }
    } catch (error) {
      console.error('Error saving indexation date:', error);
      toast.error('Fout bij opslaan van indexatiedatum');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDateValue('');
      setIsAdding(false);
    }
  };

  const handleBlur = () => {
    if (!isSaving && dateValue) {
      // Auto-save when user clicks away
      handleSave();
    } else if (!isSaving) {
      // Cancel if no value selected
      setDateValue('');
      setIsAdding(false);
    }
  };

  if (!isAdding) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm whitespace-nowrap">
          {currentDate ? formatDate(currentDate) : '-'}
        </span>
        <Tooltip
          content={
            <div className="px-1 py-2 max-w-xs">
              <div className="text-xs font-medium">
                Als u de indexatie berekent, de mail verstuurt en naar de boekhouding pusht,
                wordt deze datum automatisch bijgewerkt, maar u kan dit ook manueel toevoegen voor indexaties die in het verleden zijn gebeurd.
              </div>
            </div>
          }
          placement="top"
        >
          <Button
            size="sm"
            isIconOnly
            variant="light"
            className="min-w-6 h-6"
            onPress={() => setIsAdding(true)}
          >
            <Plus weight="bold" size={16} className="text-neutral-secondary-mid" />
          </Button>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <Input
        type="date"
        size="sm"
        className="max-w-[150px]"
        autoFocus
        value={dateValue}
        isDisabled={isSaving}
        onChange={(e) => setDateValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    </div>
  );
};

/**
 * Calculate active price (base rent) from contract products
 */
const getActivePrice = (contract: RentalContract): number => {
  return applyMask(contract.activePrice?.price || 0, 'valuta');
};

/**
 * Calculate active provision from contract products
 */
const getActiveProvision = (contract: RentalContract): number => {
  return applyMask(contract.activeProvision?.price || 0, 'valuta');
};

/**
 * Get EPC label color styling based on label
 */
const getEPCColor = (label: string): string => {
  switch (label) {
    case 'A++':
    case 'A+':
    case 'A':
      return 'bg-status-good text-white';
    case 'B':
      return 'bg-moss-primary text-white';
    case 'C':
      return 'bg-yellow-500 text-white';
    case 'D':
      return 'bg-orange-500 text-white';
    case 'E':
    case 'F':
      return 'bg-brick-primary text-white';
    case 'Gemeenschappelijke delen':
      return 'bg-neutral-primary text-white';
    default:
      return 'bg-neutral-secondary-mid text-white';
  }
};

/**
 * "Bereken" button with all indexation pre-flight checks:
 *  1. Disabled if no ondertekeningsdatum (sign date) is set
 *  2. Disabled if ondertekeningsdatum is later than the startdatum (impossible)
 *  3. Disabled if recently indexed (< 1 year ago)
 *  4. Disabled if anniversary is still more than 5 days away
 *  5. Disabled if the health index for the preceding month is not yet in the DB
 *  6. Shows a confirmation dialog when lastIndexationDate is empty (never indexed)
 */
const BerekenenButton = ({
  contract,
  availableHealthIndexMonths,
  onOpenCalculation,
}: {
  contract: RentalContract;
  availableHealthIndexMonths?: Array<{ year: number; month: number }>;
  onOpenCalculation?: (contract: RentalContract) => void;
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const anniversaryInfo = calculateAnniversaryInfo(contract, contract.lastIndexationDate);

  // Rule 1: ondertekeningsdatum (SIGN_DATE) must be filled — required input for
  // the indexation calculation, so block early with a clear reason.
  const signDate = contract.contractMilestoneDates?.find(
    (m) => m.milestoneDateType === MilestoneDateType.SIGN_DATE,
  )?.date;
  const startDate = contract.contractMilestoneDates?.find(
    (m) => m.milestoneDateType === MilestoneDateType.START_DATE,
  )?.date;
  const isSignDateMissing = !signDate;

  // Rule 2: ondertekeningsdatum after startdatum is impossible (contract must be
  // signed before — or at the latest on the day — it starts). Block and surface
  // the inconsistency so the user fixes the data.
  const isSignDateAfterStartDate =
    !isSignDateMissing &&
    !!signDate &&
    !!startDate &&
    new Date(signDate).getTime() > new Date(startDate).getTime();

  // Rule 3: already indexed recently
  const isRecent = isIndexationRecent(contract);

  // Rule 4: anniversary more than 5 days in the future
  const isTooEarly = !isRecent &&
    anniversaryInfo.anniversaryDate !== null &&
    anniversaryInfo.daysUntil > 5;

  // Rule 5: health index for the month PRECEDING the anniversary not yet in DB.
  // availableHealthIndexMonths uses 1-indexed months (Jan=1, Dec=12).
  // Returns the specific missing {year, month} so the tooltip can name it.
  const missingHealthIndexMonth: { year: number; month: number } | null = (() => {
    if (isSignDateMissing || isSignDateAfterStartDate || isRecent || isTooEarly) return null; // Other checks take priority
    if (!anniversaryInfo.anniversaryDate) return null;
    if (!availableHealthIndexMonths || availableHealthIndexMonths.length === 0) return null; // Data not loaded yet — don't block

    const ann = anniversaryInfo.anniversaryDate;
    const annMonth1indexed = ann.getMonth() + 1; // 1 = Jan, 5 = May, …

    let precedingMonth: number;
    let precedingYear: number;
    if (annMonth1indexed === 1) {
      precedingMonth = 12;
      precedingYear = ann.getFullYear() - 1;
    } else {
      precedingMonth = annMonth1indexed - 1;
      precedingYear = ann.getFullYear();
    }

    const isAvailable = availableHealthIndexMonths.some(
      (m) => m.year === precedingYear && m.month === precedingMonth,
    );
    return isAvailable ? null : { year: precedingYear, month: precedingMonth };
  })();
  const isHealthIndexMissing = missingHealthIndexMonth !== null;

  const isDisabled =
    isSignDateMissing ||
    isSignDateAfterStartDate ||
    isRecent ||
    isTooEarly ||
    isHealthIndexMissing;

  const tooltipContent = (() => {
    if (isSignDateMissing) {
      return "Ondertekeningsdatum is niet ingevuld — vul deze eerst in voordat u een indexatie berekent";
    }
    if (isSignDateAfterStartDate) {
      return "Ondertekeningsdatum ligt ná de startdatum — dat is niet mogelijk. Corrigeer de datums voor u indexeert.";
    }
    if (isRecent) return "Recent geïndexeerd";
    if (!anniversaryInfo.anniversaryDate) return "Geen startdatum ingevuld";
    if (isTooEarly) {
      return `Verjaardag is nog meer dan 5 dagen weg (${anniversaryInfo.daysUntil} dagen)`;
    }
    if (missingHealthIndexMonth) {
      // Show e.g. "Gezondheidsindex voor april 2026 is nog niet beschikbaar in het systeem".
      const monthName = new Date(
        missingHealthIndexMonth.year,
        missingHealthIndexMonth.month - 1,
        1,
      ).toLocaleDateString("nl-BE", { month: "long" });
      return `Gezondheidsindex voor ${monthName} ${missingHealthIndexMonth.year} is nog niet beschikbaar in het systeem`;
    }
    return "Bereken indexatie";
  })();

  // Open the calculation flow in a modal on the overview (no new tab). The full
  // contract — with all relations the calculate view needs — is handed over
  // directly via the callback.
  const doNavigate = () => {
    onOpenCalculation?.(contract);
  };

  const handlePress = () => {
    if (!contract.lastIndexationDate) {
      setConfirmOpen(true);
    } else {
      doNavigate();
    }
  };

  return (
    <>
      <div className="flex items-center gap-1.5">
        <Tooltip content={tooltipContent} placement="top">
          {/* Wrap disabled button in a span so the tooltip still fires on hover
              (HeroUI/Radix tooltips don't trigger reliably on disabled buttons). */}
          <span className={isDisabled ? "inline-block" : undefined}>
            <Button
              size="sm"
              variant="flat"
              color="primary"
              startContent={<Calculator weight="duotone" size={16} />}
              isDisabled={isDisabled}
              onPress={handlePress}
            >
              Bereken
            </Button>
          </span>
        </Tooltip>
        {isDisabled && (
          <Tooltip content={tooltipContent} placement="top">
            <span
              className="text-brick-primary flex h-6 w-6 shrink-0 cursor-help items-center justify-center"
              role="status"
              aria-label={tooltipContent}
            >
              <Warning weight="fill" size={16} />
            </span>
          </Tooltip>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Warning weight="fill" size={20} className="text-orange-500" />
              Geen laatste indexatiedatum ingevuld
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed">
              De kolom <strong>Laatste indexatiedatum</strong> is leeg. Weet u
              zeker dat dit contract nog nooit geïndexeerd is?
              <br /><br />
              Vul bij voorkeur eerst de laatste indexatiedatum in om onterechte
              retroactieve indexatie te vermijden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={doNavigate}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Toch doorgaan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

/**
 * Maps UnitType enum values to Dutch display labels.
 * Values are already Dutch strings so this is a passthrough with a fallback.
 */
const unitTypeToDutch = (unitType: UnitType | string | undefined): string => {
  if (!unitType) return "-";
  const map: Record<string, string> = {
    [UnitType.Residentieel]: "Residentieel",
    [UnitType.Commercieel]: "Commercieel",
    [UnitType.Overig]: "Overig",
  };
  return map[unitType] ?? unitType;
};

/**
 * Anniversary chip — uses backend-supplied daysUntilAnniversary value.
 * Suppressed when the contract started in the current calendar year
 * (Belgian rule: those contracts cannot be indexed yet).
 * Green for upcoming (≤ 10 days), red for passed (≤ 0 days), hidden otherwise.
 * Colors verified for contrast on white background:
 *   - moss-secondary-light (#F0F5E6) with moss-primary (#5B7304) text
 *   - brick-secondary-fine (#F9F0EE) with brick-primary (#EA8162) text
 */
const AnniversaryChip = ({
  daysUntilAnniversary,
  contractStartYear,
}: {
  daysUntilAnniversary: number | null | undefined;
  contractStartYear: number | null;
}) => {
  if (daysUntilAnniversary == null) return null;

  // Suppress for fresh contracts — they can't be indexed yet
  if (contractStartYear === new Date().getFullYear()) return null;

  if (daysUntilAnniversary <= 0) {
    const daysPassed = Math.abs(daysUntilAnniversary);
    return (
      <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-xs font-medium bg-brick-secondary-fine text-brick-primary whitespace-nowrap">
        {daysPassed === 0
          ? "Verjaardag vandaag"
          : `Verjaardag ${daysPassed} ${daysPassed === 1 ? "dag" : "dagen"} gepasseerd`}
      </span>
    );
  }

  if (daysUntilAnniversary <= 10) {
    return (
      <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-xs font-medium bg-moss-secondary-light text-moss-primary whitespace-nowrap">
        Verjaardag binnen {daysUntilAnniversary}{" "}
        {daysUntilAnniversary === 1 ? "dag" : "dagen"}
      </span>
    );
  }

  return null;
};

/**
 * "X maanden geleden" chip for the last-indexation cell.
 * Green when < 12 months, red when >= 12 months.
 * Hidden when monthsSinceLastIndexation is null (never indexed).
 * Colors verified against white background:
 *   green = moss-secondary-light (#F0F5E6) / moss-primary (#5B7304)
 *   red   = brick-secondary-fine (#F9F0EE) / brick-primary (#EA8162)
 */
const MonthsAgoChip = ({
  monthsSinceLastIndexation,
}: {
  monthsSinceLastIndexation: number | null | undefined;
}) => {
  if (monthsSinceLastIndexation == null) return null;

  const label =
    monthsSinceLastIndexation === 1
      ? "1 maand geleden laatst geïndexeerd"
      : `${monthsSinceLastIndexation} maanden geleden laatst geïndexeerd`;

  const isOld = monthsSinceLastIndexation >= 12;

  return (
    <span
      className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ${
        isOld
          ? "bg-brick-secondary-fine text-brick-primary"
          : "bg-moss-secondary-light text-moss-primary"
      }`}
    >
      {label}
    </span>
  );
};

/**
 * Create cell configurations for indexation table row
 */
export const createIndexationCells = (contract: RentalContract, context?: any): CellConfig[] => {
  const startDateMilestone = contract.contractMilestoneDates?.find(
    m => m.milestoneDateType === MilestoneDateType.START_DATE
  );

  const startDate = startDateMilestone?.date;
  // Derive start year for anniversary chip suppression (fresh-year contracts)
  const contractStartYear = startDate ? new Date(startDate).getFullYear() : null;
  const activePrice = getActivePrice(contract);
  const activeProvision = getActiveProvision(contract);

  // Calculate indexation status (used for the status dot only)
  const isUrgent = isIndexationUrgent(contract);
  const isRecent = isIndexationRecent(contract);

  // Get tenant contact names
  const tenants = contract.contractContacts
    ?.filter((contact: ContractContact) => contact.contactType === ContactType.TENANT)
    .map((contact: any) => contact.resource?.name)
    .filter(Boolean) || [];

  return [
    // Checkbox Column
    {
      id: 'checkbox',
      colSpan: 1,
      align: 'center' as const,
      content: (
        <Checkbox
          checked={context?.isSelected || false}
          onCheckedChange={(checked) => {
            context?.onToggleSelect?.(checked === true);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    // Status Column
    {
      id: 'status',
      colSpan: 1,
      align: 'center' as const,
      content: (
        <div className="flex items-center justify-center">
          {isUrgent ? (
            <Tooltip content="Indexatie nodig" placement="top">
              <div className="w-3 h-3 rounded-full bg-brick-primary" />
            </Tooltip>
          ) : isRecent ? (
            <Tooltip content="Recent geïndexeerd" placement="top">
              <div className="w-3 h-3 rounded-full bg-moss-primary" />
            </Tooltip>
          ) : (
            <Tooltip content="Alles in orde" placement="top">
              <div className="w-3 h-3 rounded-full bg-moss-primary" />
            </Tooltip>
          )}
        </div>
      ),
    },
    // Contract ID
    {
      id: 'contractId',
      colSpan: 1,
      content: (
        <span className="font-medium text-neutral-primary whitespace-nowrap">
          {contract.id || 'Onbekend'}
        </span>
      )
    },
    // Unit ID (with duplicate-unit warning when applicable)
    {
      id: 'unitId',
      colSpan: 1,
      content: (
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="text-sm font-medium text-neutral-primary">
            {contract.unit?.id || '-'}
          </span>
          {contract.hasDuplicateActiveContracts && (
            <TooltipProvider>
              <ShadcnTooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex shrink-0 cursor-default">
                    <Warning weight="fill" size={14} className="text-brick-primary" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Deze unit heeft meerdere actieve contracten</p>
                </TooltipContent>
              </ShadcnTooltip>
            </TooltipProvider>
          )}
        </div>
      )
    },
    // Unit Type
    {
      id: 'unitType',
      colSpan: 1,
      content: (
        <span className="text-sm text-neutral-secondary-mid whitespace-nowrap">
          {unitTypeToDutch(contract.unit?.unitType)}
        </span>
      )
    },
    // Contract Contacts (Tenants)
    {
      id: 'contractContacts',
      colSpan: 1,
      content: (
        <div className="flex flex-col gap-1 whitespace-nowrap">
          {tenants.length > 0 ? (
            contract.contractContacts
              ?.filter((contact: ContractContact) => contact.contactType === ContactType.TENANT)
              .map((contact: any, index: number) => (
                <a
                  key={index}
                  href={`/contact/${contact.resource?.resourceId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-block"
                >
                  <Chip
                    size="sm"
                    className="gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                    startContent={<UserCircle weight="duotone" size={14} />}
                    classNames={{
                      base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                      content: "px-0 font-medium text-xs whitespace-nowrap",
                    }}
                  >
                    {contact.resource?.name}
                  </Chip>
                </a>
              ))
          ) : (
            <span className="text-sm text-neutral-secondary-mid whitespace-nowrap">Geen huurder</span>
          )}
        </div>
      )
    },
    // Start Date (with anniversary chip)
    {
      id: 'startDate',
      colSpan: 1,
      content: (
        <div className="flex flex-col gap-1">
          <StartDatePicker
            contractId={contract.id}
            currentDate={startDate || null}
            milestoneId={startDateMilestone?.id}
            onDateAdded={context?.onStartDateAdded}
          />
          <AnniversaryChip
            daysUntilAnniversary={contract.daysUntilAnniversary}
            contractStartYear={contractStartYear}
          />
        </div>
      )
    },
    // Sign Date (Ondertekeningsdatum)
    {
      id: 'signDate',
      colSpan: 1,
      content: (() => {
        const signDateMilestone = contract.contractMilestoneDates?.find(
          m => m.milestoneDateType === MilestoneDateType.SIGN_DATE
        );
        const startDateMilestone = contract.contractMilestoneDates?.find(
          m => m.milestoneDateType === MilestoneDateType.START_DATE
        );
        const signAfterStart =
          !!signDateMilestone?.date &&
          !!startDateMilestone?.date &&
          new Date(signDateMilestone.date).getTime() >
            new Date(startDateMilestone.date).getTime();

        return (
          <div className="flex items-center gap-1.5">
            <SignDatePicker
              contractId={contract.id}
              currentDate={signDateMilestone?.date || null}
              milestoneId={signDateMilestone?.id}
              onDateAdded={context?.onSignDateAdded}
            />
            {signAfterStart && (
              <Tooltip
                content="Ondertekeningsdatum ligt ná de startdatum — dat is niet mogelijk. Corrigeer de datums voor u indexeert."
                placement="top"
              >
                <span
                  className="text-brick-primary flex h-5 w-5 shrink-0 cursor-help items-center justify-center"
                  role="status"
                  aria-label="Ondertekeningsdatum ligt na de startdatum"
                >
                  <Warning weight="fill" size={14} />
                </span>
              </Tooltip>
            )}
          </div>
        );
      })()
    },
    // Last Indexation Date (with months-ago chip)
    {
      id: 'lastIndexationDate',
      colSpan: 1,
      content: (
        <div className="flex flex-col gap-1">
          <IndexationDatePicker
            contractId={contract.id}
            currentDate={contract.lastIndexationDate || null}
            onDateAdded={context?.onIndexationDateAdded}
          />
          <MonthsAgoChip
            monthsSinceLastIndexation={contract.monthsSinceLastIndexation}
          />
        </div>
      )
    },
    // INIT Price (Initial contract price from ledger 700001 or 700002)
    {
      id: 'initPrice',
      colSpan: 1,
      content: (
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="text-sm font-medium">
            {contract.initPrice ? applyMask(contract.initPrice, 'valuta') : '-'}
          </span>
          {context?.onProductPriceUpdated && (
            <span onClick={(e) => e.stopPropagation()}>
              <PriceEditButton
                contract={contract}
                mode="base"
                onUpdated={context.onProductPriceUpdated}
              />
            </span>
          )}
        </div>
      )
    },
    // Active Price (Huidige prijs)
    {
      id: 'activePrice',
      colSpan: 1,
      content: (() => {
        const basePriceRaw = contract.initPrice ?? 0;
        const activePriceRaw = contract.activePrice?.price ?? 0;
        const showPriceAnomaly =
          basePriceRaw > 0 && activePriceRaw > 0 && basePriceRaw >= activePriceRaw;
        return (
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="text-sm font-medium">{activePrice}</span>
            {showPriceAnomaly && (
              <TooltipProvider delayDuration={200}>
                <ShadcnTooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex shrink-0 cursor-default">
                      <Warning
                        weight="fill"
                        size={16}
                        className="text-brick-primary"
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Basishuurprijs is gelijk aan of hoger dan huidige huurprijs — controleer</p>
                  </TooltipContent>
                </ShadcnTooltip>
              </TooltipProvider>
            )}
            {context?.onProductPriceUpdated && (
              <span onClick={(e) => e.stopPropagation()}>
                <PriceEditButton
                  contract={contract}
                  mode="current"
                  onUpdated={context.onProductPriceUpdated}
                />
              </span>
            )}
          </div>
        );
      })()
    },
    // Active Provision (Huidige provisie)
    {
      id: 'activeProvision',
      colSpan: 1,
      content: (
        <span className="text-sm font-medium whitespace-nowrap">
          {activeProvision}
        </span>
      )
    },
    // Actions (Indexatie)
    {
      id: 'actions',
      colSpan: 1,
      align: 'center' as const,
      content: (
        <div className="whitespace-nowrap">
          {context?.onOpenCalculation ? (
            <BerekenenButton
              contract={contract}
              availableHealthIndexMonths={context?.availableHealthIndexMonths}
              onOpenCalculation={context.onOpenCalculation}
            />
          ) : (
            <Button
              size="sm"
              variant="flat"
              color="primary"
              startContent={<Calculator weight="duotone" size={16} />}
              isDisabled
            >
              Bereken
            </Button>
          )}
        </div>
      )
    },
    // Current EPC
    {
      id: 'currentEPC',
      colSpan: 1,
      content: contract.currentEPC ? (
        <a
          href={contract.currentEPC.sharePointFileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`px-2 py-1 rounded-sm text-white font-medium cursor-pointer hover:opacity-80 transition-opacity ${getEPCColor(contract.currentEPC.label)}`}
          onClick={(e) => e.stopPropagation()}
        >
          {contract.currentEPC.label}
        </a>
      ) : (
        <span className="text-sm text-neutral-secondary-mid">-</span>
      )
    },
    // Contract File (Huurcontract)
    {
      id: 'contractFile',
      colSpan: 1,
      align: 'center' as const,
      content: (
        <div className="flex items-center justify-center shrink-0 min-w-[20px]">
          {contract.huurcontractFileLog ? (
            <a
              href={contract.huurcontractFileLog.sharePointFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-moss-primary hover:text-moss-primary/80 transition-colors cursor-pointer flex items-center justify-center shrink-0"
              onClick={(e) => e.stopPropagation()}
              title="Open huurcontract"
            >
              <FileText size={18} weight="duotone" className="shrink-0" />
            </a>
          ) : (
            <span className="text-sm text-neutral-secondary-mid shrink-0">-</span>
          )}
        </div>
      )
    }
  ];
};
